import React from 'react';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import { People as PeopleIcon, Storage as StorageIcon } from '@mui/icons-material';
import { ProgressBar } from 'new/elements/ProgressBar';
import {
  useFetchAdminOrgAccountMetrics,
  useFetchAdminOrgAccountSummary,
  useFetchAdminOrgMembers,
} from 'new/stores/adminOrgs/adminOrgs.api';
import { AdminOrg } from 'new/stores/adminOrgs/adminOrgs.types';
import { formatBytes, formatCompactNumber, formatLimit, sumAccountMetrics } from '../../utils';
import tabStyles from './tabs.module.scss';

interface OverviewTabProps {
  org: AdminOrg;
}

function isoDaysAgo(days: number) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

function MeterRow({
  icon,
  label,
  used,
  limit,
}: {
  icon: React.ReactNode;
  label: string;
  used: number;
  limit: number;
}) {
  const unlimited = limit == null || limit < 0;
  return (
    <div className={tabStyles.meterRow}>
      <Stack direction="row" alignItems="center" spacing={1} className={tabStyles.meterLabel}>
        {icon}
        <Typography variant="body2">{label}</Typography>
      </Stack>
      <Typography variant="body3" className={tabStyles.meterValue}>
        {formatCompactNumber(used)} / {unlimited ? 'Unlimited' : formatCompactNumber(limit)}
      </Typography>
      {!unlimited && <ProgressBar current={used} total={limit} />}
    </div>
  );
}

export function OverviewTab({ org }: OverviewTabProps) {
  const { data: members = [] } = useFetchAdminOrgMembers(org.id);
  const { data: summary } = useFetchAdminOrgAccountSummary(org.id);
  const { data: usageMetrics } = useFetchAdminOrgAccountMetrics({
    orgId: org.id,
    from: isoDaysAgo(30),
  });

  const usage = sumAccountMetrics(usageMetrics?.metrics);
  const activeMembers = members.filter((m) => m.org_membership_status === 'ACTIVE');
  const admins = activeMembers.filter((m) => m['is_admin?']);

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2}>
        <Card variant="outlined" className={tabStyles.statCard}>
          <CardContent>
            <Typography variant="body3" color="text.secondary">
              Members
            </Typography>
            <Typography variant="h5">{activeMembers.length}</Typography>
            <Typography variant="body3" color="text.secondary">
              {admins.length} admins
            </Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" className={tabStyles.statCard}>
          <CardContent>
            <Typography variant="body3" color="text.secondary">
              Usage (last 30 days)
            </Typography>
            <Typography variant="h5">{formatCompactNumber(usage.records)} records</Typography>
            <Typography variant="body3" color="text.secondary">
              {formatBytes(usage.size)} processed
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Usage vs limits
          </Typography>
          <Stack spacing={2.5} className={tabStyles.meterList}>
            <MeterRow
              icon={<PeopleIcon fontSize="small" />}
              label="Seats"
              used={activeMembers.length}
              limit={org.org_tier?.user_members_limit ?? -1}
            />
            <MeterRow
              icon={<StorageIcon fontSize="small" />}
              label="Data sources"
              used={summary?.data_sources?.total ?? 0}
              limit={org.org_tier?.data_source_count_limit ?? -1}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2">
                Records limit ({org.org_tier?.record_count_limit_time?.toLowerCase() ?? 'n/a'})
              </Typography>
              <Typography variant="body3" color="text.secondary">
                {formatLimit(org.org_tier?.record_count_limit)}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
